import { io } from 'socket.io-client';

/*
 * Transport
 *
 * The socket.io connection to the GRACI assistant, and the whole of what this
 * folder knows about the wire.
 *
 * The protocol is documented in README.md. Two things about it shape everything
 * here:
 *
 *   1. THE SERVER RETURNS THE WHOLE CONVERSATION, not a delta. So the client
 *      replaces its list rather than appending to it, and the server is the
 *      single source of truth for what was said. Optimistic local messages are
 *      drawn until the next reply arrives and are then thrown away.
 *
 *   2. Assistant content arrives JSON encoded. A parse failure is not fatal:
 *      the raw string is shown instead, because a customer reading a slightly
 *      odd message is a better outcome than a blank screen.
 *
 * NO AUTHENTICATION. Unlike every other namespace on that server, `/graci`
 * has no middleware on it. It is built for anonymous origins, which is exactly
 * what a public support centre is, so nothing is sent and nothing is expected.
 */

const NAMESPACE = '/graci';
export const INPUT = 'new_support_center_chat_completion_message_input';
export const OUTPUT = 'new_chat_completion_message_output';

/*
 * Machinery, not conversation.
 *
 * Three kinds of message come down the same pipe as the answer. The hidden
 * priming messages carry the system prompt and the session variables. The tool
 * results carry raw API payloads. And when the model calls a tool it first
 * returns an assistant message with `tool_calls` and NO CONTENT, which is left
 * in the array alongside the real reply that follows it. That last one is the
 * easy one to miss: it passes a `visible !== false` check happily and draws an
 * empty bubble above every answer that involved a lookup.
 */
export const isVisible = message => {
  if (message?.visible === false || message?.role === 'tool') {
    return false;
  }
  if (message?.role === 'assistant' && !message?.content) {
    return false;
  }
  return true;
};
const parsed = message => {
  if (message?.role === 'user' || typeof message?.content !== 'string') {
    return message;
  }
  try {
    return {
      ...message,
      content: JSON.parse(message.content)
    };
  } catch (e) {
    // Not JSON. Show what came down rather than nothing at all.
    return message;
  }
};

/*
 * The assistant answers in a fixed JSON schema, not in prose.
 *
 *   { data_input_request, message, sections: [{ header, sub_header,
 *     list_items: [{ key, value }], video_tutorial_links: [{ title, url }] }] }
 *
 * `message` is the summary and is often the whole answer. `sections` is where
 * troubleshooting steps, specifications and video links arrive, and a renderer
 * that reads only `message` drops all of it without leaving a trace.
 *
 * Normalised here rather than in the view so one place knows the schema, and so
 * a plain string still renders if the model ever returns one.
 */
export const contentOf = message => {
  let content = message?.content;
  if (typeof content === 'string') {
    return {
      message: content,
      request: null,
      sections: []
    };
  }
  return {
    message: content?.message || null,
    request: content?.data_input_request || null,
    sections: (content?.sections || []).filter(Boolean)
  };
};

/*
 * `list_items` carries two different things under one shape.
 *
 * Sometimes it is a procedure, where `key` numbers the step. Sometimes it is a
 * set of readings, where `key` is "Serial number" and `value` is the answer.
 * They want drawing differently: numbering a set of readings implies an order
 * that is not there, and a procedure drawn as readings loses the order that is.
 *
 * A step key comes in two forms and the second is the common one:
 *
 *     "3"                    a bare number
 *     "3. Testing the sensor" a number and a title
 *
 * The first version of this only recognized the bare form, so every real answer
 * fell through to the readings layout and a set of troubleshooting steps came
 * out as a specifications table with the numbering buried in a grey label.
 *
 * Deliberately strict about what a leading number is. "1st" and "Step 1" are
 * words a person wrote, and a list of those is a list of labels.
 */
const BARE_STEP = /^\s*(\d+)\s*[).:\-\u2013]?\s*$/;
const TITLED_STEP = /^\s*(\d+)\s*[).:\-\u2013]?\s+(\S.*)$/;

// { number, label } for a step key, or null when it is not one.
export const stepParts = key => {
  let text = String(key ?? '');
  let bare = BARE_STEP.exec(text);
  if (bare) {
    return {
      label: null,
      number: bare[1]
    };
  }
  let titled = TITLED_STEP.exec(text);
  if (titled) {
    return {
      label: titled[2].trim(),
      number: titled[1]
    };
  }
  return null;
};
export const isStepList = items => {
  return Array.isArray(items) && items.length > 0 && items.every(item => stepParts(item?.key) !== null);
};
export const connect = ({
  onError,
  onOutput,
  url
}) => {
  let socket = io(`${url}${NAMESPACE}`, {
    reconnectionAttempts: 3,
    transports: ['websocket', 'polling']
  });
  socket.on(OUTPUT, event => {
    onOutput({
      error: event?.error || null,
      id: event?.id || null,
      messages: (event?.messages || []).map(parsed)
    });
  });
  socket.on('connect_error', error => {
    onError(error?.message || 'We could not reach the assistant.');
  });
  return {
    close: () => {
      socket.removeAllListeners();
      socket.close();
    },
    /*
     * `session` is required on every send: the server iterates its keys
     * without guarding, so omitting it throws server side rather than here.
     *
     * `instructions` is a fixed vocabulary of typed objects, not free text.
     * The server switches on `instruction.type` and knows four of them. Any
     * context outside that vocabulary belongs in `session`, which the
     * server renders into a hidden priming message. See Context.js.
     */
    send: ({
      id,
      instructions,
      language,
      messages,
      session
    }) => {
      socket.emit(INPUT, {
        id: id,
        instructions: instructions,
        language: language || 'en',
        messages: messages,
        session: session || {}
      });
    }
  };
};
const Transport = {
  INPUT,
  OUTPUT,
  connect,
  contentOf,
  isStepList,
  isVisible,
  stepParts
};
export default Transport;